import {DatePicker, Form, Input, InputNumber, Select, Spin, Switch} from 'antd';
import {groupThousands, parseThousands} from '../../v2/numberFormat';
import styles from '../assetInstrument.module.scss';
import {
  bondTypes,
  couponFrequency,
} from 'pages/AssetManagement/modules/constants';
import {getUserTimezone} from 'utils/helper';
import moment from 'moment';
import v2 from '../../v2/instrumentV2.module.scss';
import {FormCard, Hint} from '../../v2/FormCard';
import {InstrumentActions} from '../../v2/InstrumentActions';
import {useRemainingFields} from '../../v2/useRemainingFields';
import {BOND_DETAILS_MOCK} from '../../v2/devPrefill';
import {useDispatch, useSelector} from 'react-redux';
import {handleBondsFieldState} from 'redux/store/assetClass';
import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {createBondsStepTwoRoute} from '../routes';
import {useMutation, useQuery} from '@apollo/client';
import {
  GET_ALL_RATINGS,
  GET_BOND_INSTRUMENT_BY_LANGUAGE,
  GET_BOND_TYPE,
} from 'graphql/queries/assetManagement';
import {UPDATE_BOND} from 'graphql/mutations/assetManagement';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {handleUpdateBond} from 'pages/AssetManagement/actions';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';

const BondsStepTwoOverview = () => {
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [updateData, setUpdateData] = useState({});
  const [updatedValues, setUpdatedValues] = useState({});
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const {assetId, id, lang} = useParams();

  /**
   * The footer said "All required fields on this screen are complete" on a
   * blank form — `InstrumentActions` defaults `remaining` to 0 and this screen
   * never passed it, so the bar was hard-coded to congratulate you while twelve
   * fields sat empty and Save & Proceed bounced off validation.
   *
   * These are exactly the names carrying `required: true` below.
   */
  const REQUIRED_FIELDS = [
    'ticker',
    'type',
    'maturityDate',
    'issuanceDate',
    'tenor',
    'yieldToMaturity',
    'couponRate',
    'currentMarketPrice',
    'couponFrequency',
    'gracePeriodPrincipal',
    'gracePeriodInterest',
    'managementFee',
  ];
  const [remaining, countRemaining] = useRemainingFields(
    form,
    REQUIRED_FIELDS,
    id,
  );
  const navigate = useNavigate();
  const bondsData = useSelector(state => state?.assetClass?.bonds);

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const [updateBond, {loading: loadingUpdate}] = useMutation(UPDATE_BOND);
  const {refetch, loading: loadingQuery} = useQuery(
    GET_BOND_INSTRUMENT_BY_LANGUAGE,
    {
      variables: {
        getFinancialInstrumentId: id,
        language: lang,
      },
      skip: !id,
      onCompleted: data => setUpdateData(data?.getFinancialInstrument?.data),
    },
  );

  // Isolated so the BondType enum defect cannot take the whole screen with it.
  // When the enum rejects a stored value the field serialises to null, but the
  // GraphQL error still names the value — so we can at least show the operator
  // what is on the record rather than an unexplained blank.
  // With errorPolicy 'all' Apollo resolves rather than throwing, so read the
  // error off the hook result instead of relying on onError firing.
  const {error: bondTypeError} = useQuery(GET_BOND_TYPE, {
    variables: {getFinancialInstrumentId: id},
    skip: !id,
    errorPolicy: 'all',
    onCompleted: res => {
      const value = res?.getFinancialInstrument?.data?.type;
      if (value) form.setFieldValue('type', value);
    },
  });

  const storedBondType =
    bondTypeError?.graphQLErrors
      ?.map(e => /cannot represent value: "?([A-Z_]+)"?/.exec(e?.message)?.[1])
      ?.find(Boolean) ?? null;

  const {data, loading} = useQuery(GET_ALL_RATINGS, {
    variables: {
      page: 1,
      limit: 100,
    },
  });

  const handleSubmit = values => {
    // `length` and `period` are UI-only helpers for computing tenor.
    // `ticker` and `isin` are collected here but belong to the base instrument
    // input, not UpdateBondInput — sending them flat is rejected outright.
    // eslint-disable-next-line no-unused-vars
    const {length, period, ticker, isin, ...bondFields} = values;
    const restValues = {
      ...bondFields,
      baseFinancialInstrumentInput: {ticker, isin},
    };

    if (id) {
      setOpenUpdateModal(true);
      setUpdatedValues(restValues);
    } else {
      // The shared draft slice stays flat — CreateBondFinishModal reads ticker
      // and isin off it directly, and a nested key would hide them.
      dispatch(
        handleBondsFieldState({
          ...bondFields,
          ticker,
          isin,
        }),
      );
      navigate(createBondsStepTwoRoute({param: assetId, name: 'broker'}));
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    countRemaining();
    const {length, period} = allValues;

    if (length != null && period) {
      // Calculate the target date based on today's date with dynamic period calculation
      const targetDate = moment().add(length, period);
      // Calculate the difference in days from today to the target date
      const daysFromToday = targetDate.diff(moment(), 'days');

      // Update the form field with calculated days
      if (!Object.keys(changedValues).includes('tenor'))
        form.setFieldValue('tenor', daysFromToday);
    }
  };

  useEffect(() => {
    const dataToFetch = id ? updateData || {} : bondsData;
    const {
      ticker,
      isin,
      tenor,
      couponRate,
      couponFrequency,
      yieldToMaturity,
      rating,
      callable,
      maturityDate,
      currentMarketPrice,
      faceValue,
      issuanceDate,
      managementFee,
      gracePeriodPrincipal,
      gracePeriodInterest,
      nextCallDate,
      nextCouponDate,
      issuanceVolume,
    } = dataToFetch;
    // Guarded on `ticker`, not `type`: `type` is held out of the bond query
    // because BondType rejects the stored GREEN and SUKUK values, so gating on
    // it meant the form never populated on update at all.
    if (dataToFetch?.ticker || dataToFetch?.id) {
      form.setFieldsValue({
        // `type` deliberately omitted — it comes from its own query.
        ticker,
        isin,
        tenor,
        couponRate,
        managementFee,
        gracePeriodPrincipal,
        gracePeriodInterest,
        maturityDate: moment(maturityDate, 'YYYY-MM-DD HH:mm:ss'),
        issuanceDate: moment(issuanceDate, 'YYYY-MM-DD HH:mm:ss'),
        nextCallDate: nextCallDate
          ? moment(nextCallDate, 'YYYY-MM-DD HH:mm:ss')
          : null,
        nextCouponDate: nextCouponDate
          ? moment(nextCouponDate, 'YYYY-MM-DD HH:mm:ss')
          : null,
        couponFrequency,
        yieldToMaturity,
        rating: rating?.id ?? rating,
        callable,
        currentMarketPrice,
        faceValue,
        issuanceVolume,
      });
    }
  }, [updateData, bondsData]);

  if (loadingQuery) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Bond details</h2>
        <p>
          Terms of the issue. These drive the yield and payment figures shown to
          investors.
        </p>
      </div>

      <Form
        onValuesChange={handleValuesChange}
        onFinish={handleSubmit}
        layout="vertical"
        form={form}>
        <FormCard
          title="Basic information"
          description="Identifier and classification of the issue">
          <div className={v2.fieldRow}>
            <Form.Item
              rules={[{required: true, message: 'Ticker is required'}]}
              className={styles.smallWidth}
              name="ticker"
              label="Ticker">
              <Input placeholder="BOAB" />
            </Form.Item>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              className={styles.smallWidth}
              name="type"
              label="Bond Type">
              <Select placeholder="Select a Type">
                {bondTypes.map(data => (
                  <Select.Option key={data.value}>{data.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            {storedBondType && (
              <Hint>
                Stored as <strong>{storedBondType}</strong>, which the
                API&apos;s BondType enum does not yet accept — so it cannot be
                loaded into the field. Leave this blank to keep it, or pick a
                value to overwrite it.
              </Hint>
            )}
          </div>
          <div className={v2.fieldRow}>
            <Form.Item
              className={styles.smallWidth}
              name="isin"
              label="ISIN (International Securities Identification Number)">
              <Input />
            </Form.Item>
          </div>
        </FormCard>
        <FormCard
          title="Key dates"
          description="Issuance, maturity and the next scheduled events">
          <div className={v2.fieldRow}>
            <Form.Item
              className={styles.smallWidth}
              name="maturityDate"
              rules={[{required: true, message: 'This is a Required Field'}]}
              label="Maturity Date">
              <DatePicker
                className={styles.smallWidth}
                renderExtraFooter={() => (
                  <div style={{textAlign: 'center'}}>
                    <p>Your timezone is: {getUserTimezone()}</p>
                  </div>
                )}
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                format="DD MMM YYYY, HH:mm"
              />
            </Form.Item>
            <Form.Item
              className={styles.smallWidth}
              name="nextCouponDate"
              label="Next Coupon Date">
              <DatePicker
                className={styles.smallWidth}
                renderExtraFooter={() => (
                  <div style={{textAlign: 'center'}}>
                    <p>Your timezone is: {getUserTimezone()}</p>
                  </div>
                )}
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                format="DD MMM YYYY, HH:mm"
              />
            </Form.Item>
          </div>
          <div className={v2.fieldRow}>
            <Form.Item
              className={styles.smallWidth}
              name="issuanceDate"
              rules={[{required: true, message: 'This is a Required Field'}]}
              label="Issuance Date">
              <DatePicker
                className={styles.smallWidth}
                renderExtraFooter={() => (
                  <div style={{textAlign: 'center'}}>
                    <p>Your timezone is: {getUserTimezone()}</p>
                  </div>
                )}
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                format="DD MMM YYYY, HH:mm"
              />
            </Form.Item>
            <Form.Item
              className={styles.smallWidth}
              name="nextCallDate"
              label="Next Call Date">
              <DatePicker
                className={styles.smallWidth}
                renderExtraFooter={() => (
                  <div style={{textAlign: 'center'}}>
                    <p>Your timezone is: {getUserTimezone()}</p>
                  </div>
                )}
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                format="DD MMM YYYY, HH:mm"
              />
            </Form.Item>
          </div>
        </FormCard>
        <FormCard
          title="Financial metrics"
          description="Pricing, yield and the terms investors are quoted">
          <div className={styles.durationField}>
            <div className={styles.durationMultiplier}>
              <div className={styles.multiplier}>
                <Form.Item name="length" label="Duration Length">
                  <InputNumber className={styles.multiplierNumber} />
                </Form.Item>
                <Form.Item name="period" label="Duration Period">
                  <Select allowClear placeholder="Select Period">
                    <Select.Option value="day">Day</Select.Option>
                    <Select.Option value="week">Week</Select.Option>
                    <Select.Option value="month">Month</Select.Option>
                    <Select.Option value="year">Year</Select.Option>
                  </Select>
                </Form.Item>
              </div>
              <Form.Item
                className={styles.smallWidth}
                rules={[{required: true, message: 'This is a Required Field'}]}
                name="tenor"
                label="Total Duration in DAYS">
                <InputNumber
                  className={styles['input-number']}
                  formatter={groupThousands}
                  parser={parseThousands}
                  placeholder="No. of DAYS"
                />
              </Form.Item>
            </div>
          </div>
          <div className={v2.fieldRow}>
            <Form.Item
              className={styles.smallWidth}
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="yieldToMaturity"
              label="Yield to Maturity">
              <InputNumber
                className={styles['input-number']}
                formatter={groupThousands}
                parser={parseThousands}
              />
            </Form.Item>
            <Form.Item
              className={styles.smallWidth}
              name="couponRate"
              rules={[{required: true, message: 'This is a Required Field'}]}
              label="Coupon Rate">
              <InputNumber
                className={styles['input-number']}
                formatter={groupThousands}
                parser={parseThousands}
              />
            </Form.Item>
          </div>
          <div className={v2.fieldRow}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              className={styles.smallWidth}
              name="currentMarketPrice"
              label="Current Market Price">
              <InputNumber
                className={styles['input-number']}
                formatter={groupThousands}
                parser={parseThousands}
              />
            </Form.Item>
            <Form.Item name="faceValue" label="Face Value">
              <InputNumber
                className={styles['input-number']}
                formatter={groupThousands}
                parser={parseThousands}
              />
            </Form.Item>
          </div>
          <div className={v2.fieldRow}>
            <Form.Item
              name="couponFrequency"
              className={styles.smallWidth}
              rules={[{required: true, message: 'This is a Required Field'}]}
              label="Interest Payment Frequency">
              <Select allowClear>
                {couponFrequency.map(data => (
                  <Select.Option key={data.value}>{data.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="issuanceVolume" label="Issuance Volume">
              <InputNumber
                className={styles['input-number']}
                formatter={groupThousands}
                parser={parseThousands}
              />
            </Form.Item>
          </div>
          <Form.Item
            className={styles.smallWidth}
            name="rating"
            label="Ratings">
            <Select loading={loading} allowClear>
              {data?.getAllRatings?.data?.map(rate => (
                <Select.Option key={rate?.id}>{rate?.agencyName}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="callable"
            valuePropName="checked"
            tooltip="Can be redeemed by the issuer before maturity"
            label="Callable">
            <Switch
              defaultChecked={bondsData?.callable || updateData?.callable}
            />
          </Form.Item>
        </FormCard>
        <FormCard
          title="Calculator metrics"
          description="Grace periods and fees used by the returns calculator">
          <div className={v2.fieldRow}>
            <Form.Item
              className={styles.smallWidth}
              name="gracePeriodPrincipal"
              rules={[{required: true, message: 'This is a Required Field'}]}
              label="Grace Period Principal (Days)">
              <InputNumber
                className={styles['input-number']}
                formatter={groupThousands}
                parser={parseThousands}
                placeholder="No. of DAYS"
              />
            </Form.Item>
            <Form.Item
              className={styles.smallWidth}
              name="gracePeriodInterest"
              rules={[{required: true, message: 'This is a Required Field'}]}
              label="Grace Period Interest (Days)">
              <InputNumber
                className={styles['input-number']}
                formatter={groupThousands}
                parser={parseThousands}
                placeholder="No. of DAYS"
              />
            </Form.Item>
          </div>

          <Form.Item
            className={styles.smallWidth}
            name="managementFee"
            rules={[{required: true, message: 'This is a Required Field'}]}
            label="Management Fee (%)">
            <InputNumber
              className={styles['input-number']}
              formatter={groupThousands}
              parser={parseThousands}
            />
          </Form.Item>
        </FormCard>
        <InstrumentActions
          remaining={remaining}
          // `setFieldsValue` fires neither `onValuesChange` nor a `syncKey`
          // change, so the count has to be asked for explicitly or the bar
          // still reports the pre-fill state.
          onPrepopulate={() => {
            form.setFieldsValue(BOND_DETAILS_MOCK);
            countRemaining();
          }}
          disabled={!pageOperations?.update}
          label="Save & Proceed"
        />
      </Form>
      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        title="Update Bond"
        confirmText="Are you sure you want to update this bond?"
        handleOperation={() =>
          handleUpdateBond(
            updateBond,
            id,
            lang,
            updatedValues,
            refetch,
            setOpenUpdateModal,
            true,
          )
        }
      />
    </div>
  );
};

export default BondsStepTwoOverview;
